import { Skeleton } from "@mantine/core";
import { useLocation } from "react-router-dom";
import { CategorySkeleton } from "@/components/category/CategorySkeleton";
import { CuratedSkeleton } from "@/components/curated/CuratedSkeleton";
import styles from "./styles/RouteSkeleton.module.css";

const placeholders = (count: number) => Array.from({ length: count }, (_, i) => i);

const EpisodeRowsSkeleton = ({ count }: { count: number }) => (
    <div className={styles.rows}>
        {placeholders(count).map((i) => (
            <div className={styles.row} key={i}>
                <Skeleton className={styles.thumb} radius="md" />
                <div className={styles.rowText}>
                    <Skeleton height={16} width="70%" />
                    <Skeleton height={12} width="40%" />
                </div>
            </div>
        ))}
    </div>
);

/** The podcast page: breadcrumb row, hero (cover and text), then the episode card with its search and first rows (#329) */
const PodcastSkeleton = () => (
    <div aria-busy="true" aria-label="Laddar podcast">
        <div className={styles.podCrumbs} />
        <div className={styles.podHero}>
            <Skeleton className={styles.podCover} radius="lg" />
            <div className={styles.podHeroText}>
                <Skeleton className={styles.podTitle} width="75%" />
                <Skeleton height={14} width="45%" />
                <Skeleton height={14} width="90%" />
                <Skeleton height={14} width="70%" />
            </div>
        </div>
        <div className={styles.podCard}>
            <Skeleton className={styles.podCardHead} width={89} />
            <Skeleton className={styles.podSearch} radius="md" />
            <div className={styles.podRows}>
                <EpisodeRowsSkeleton count={6} />
            </div>
        </div>
    </div>
);

/** The toplist: a header card, the podium of the top three, then the ranked list. Heights match the page at phone and desktop widths (#329). */
const ToplistSkeleton = () => (
    <div aria-busy="true" aria-label="Laddar topplistan">
        <div className={styles.toplistCrumbs} />
        <div className={styles.toplistHero}>
            <Skeleton height={14} width="30%" />
            <Skeleton className={styles.toplistTitle} width="55%" />
            <Skeleton height={14} width="95%" />
            <Skeleton height={14} width="70%" />
            <Skeleton className={styles.toplistSearch} radius="md" />
        </div>
        <div className={styles.toplistHeading}>
            <Skeleton height={20} width={98} />
        </div>
        <div className={styles.toplistPodium}>
            {placeholders(3).map((i) => (
                <Skeleton key={i} className={styles.toplistPodiumCard} radius="lg" />
            ))}
        </div>
        <div className={`${styles.toplistHeading} ${styles.toplistHeadingLast}`}>
            <Skeleton height={20} width={220} />
        </div>
        <div className={styles.toplistRows}>
            {placeholders(12).map((i) => (
                <Skeleton key={i} height={33} />
            ))}
        </div>
    </div>
);

const GridSkeleton = () => (
    <div aria-busy="true" aria-label="Laddar sidan">
        <Skeleton height={40} mb="lg" width="40%" />
        <div className={styles.grid}>
            {placeholders(12).map((i) => (
                <div key={i}>
                    <Skeleton className={styles.square} radius="md" />
                    <Skeleton height={14} mt={8} width="80%" />
                </div>
            ))}
        </div>
    </div>
);

/** Fallback shaped like the page being loaded, so content does not jump in. Shown inside Layout's container. */
export const RouteSkeleton = () => {
    const { pathname } = useLocation();

    return (
        <>
            {pathname.startsWith("/kategori/") ? (
                <CategorySkeleton />
            ) : pathname.startsWith("/samling/") ? (
                <CuratedSkeleton label="Laddar samling" variant="list" />
            ) : pathname.startsWith("/fokus/") ? (
                <CuratedSkeleton label="Laddar fokusområde" variant="list" />
            ) : pathname === "/samling" || pathname === "/fokus" ? (
                <CuratedSkeleton label="Laddar" variant="tiles" />
            ) : pathname.startsWith("/topplista") ? (
                <ToplistSkeleton />
            ) : pathname.startsWith("/podd/") ? (
                <PodcastSkeleton />
            ) : (
                <GridSkeleton />
            )}
        </>
    );
};
